import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { Observable } from 'rxjs';

import { IChatAttachment, IChatDraft, IChatMessage, ngHelper } from '@cakeiteasy/web-utils';

import { ChatSocketService } from '../../services/chat-socket.service';
import { IChatConversation } from '../../models/i-chat-conversation.interface';
import { ChatImageUploadService } from '../../services/chat-image-upload.service';
import { OrderChatManagerService } from '../../services/order-chat-manager.service';

/**
 * A whole chat conversation: message list, composer, connection banners and error states.
 *
 * Deliberately does not provide `CHAT_CONVERSATION_GATEWAY` — the REST side differs per screen
 * (a header-identified order request here, a route-identified order there), so the host provides
 * it, and this component resolves it from the host's injector.
 */
@Component({
  standalone: false,
  selector: 'order-chat-conversation',
  templateUrl: './order-chat-conversation.component.html',
  styleUrls: ['./order-chat-conversation.component.scss'],
  providers: [ChatSocketService, ChatImageUploadService, OrderChatManagerService],
})
export class OrderChatConversationComponent implements OnChanges {
  /** Null until the host knows which conversation this is; opening waits for it. */
  @Input() conversation: IChatConversation;
  @Input() placeholder = '';

  /** Every pending operation the host may want to show a spinner for. */
  @Output() busyChange = new EventEmitter<Promise<unknown>>();

  public readonly messages$: Observable<IChatMessage[]>;
  public readonly reconnecting$: Observable<boolean>;
  public readonly connectionLost$: Observable<boolean>;
  public readonly sendError$: Observable<string>;
  public readonly loadError$: Observable<boolean>;
  public readonly sending$: Observable<boolean>;
  public readonly draftResetKey$: Observable<number>;

  // Bound once: `imageUploader.bind(this)` in a template allocates a new function on every
  // change detection cycle, which re-triggers ui-chat's ngOnChanges for no reason.
  public readonly imageUploader = (img: string) => this._uploadImage(img);

  constructor(
    private _chatManager: OrderChatManagerService,
    private _imageUpload: ChatImageUploadService,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    this.messages$ = _chatManager.messages$;
    this.reconnecting$ = _chatManager.reconnecting$;
    this.connectionLost$ = _chatManager.connectionLost$;
    this.sendError$ = _chatManager.sendError$;
    this.loadError$ = _chatManager.loadError$;
    this.sending$ = _chatManager.sending$;
    this.draftResetKey$ = _chatManager.draftResetKey$;
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'conversation', () => this._open());
  }

  public sendMessage(draft: IChatDraft): Promise<void> {
    return this._chatManager.sendMessage(draft);
  }

  public reloadPage() {
    this._document.defaultView.location.reload();
  }

  private _open() {
    if (!this.conversation) {
      return;
    }

    const { parentOrderId, customerKey } = this.conversation;

    this.busyChange.emit(this._chatManager.open(parentOrderId, customerKey));
  }

  private _uploadImage(img: string): Promise<IChatAttachment> {
    const uploading = this._imageUpload.uploadImage(img);

    this.busyChange.emit(uploading);

    return uploading;
  }
}
